import React from "react";
import "./gradient-background.scss";

/**
 * GradientEffects - Fixed positioned animated effects
 * Includes SVG waves and blur orbs for a futuristic look.
 * 
 * This component is rendered inside AppLayout to avoid z-index stacking context issues.
 * All elements use position: fixed and are positioned relative to the viewport.
 */
const GradientEffects: React.FC = () => {
  return (
    <>
      {/* Animated SVG waves at the top */}
      <svg
        className="animated-waves"
        width="100%"
        height="100%"
        viewBox="0 0 1200 300"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Light mode gradients */}
          <linearGradient id="lightGrad1" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop
              offset="0%"
              style={{ stopColor: "rgb(0, 102, 204)", stopOpacity: 0.8 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: "rgb(0, 153, 255)", stopOpacity: 0.4 }}
            />
          </linearGradient>
          <linearGradient id="lightGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop
              offset="0%"
              style={{ stopColor: "rgb(85, 85, 255)", stopOpacity: 0.7 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: "rgb(0, 136, 204)", stopOpacity: 0.4 }}
            />
          </linearGradient>
          
          {/* Dark mode gradients - brighter purple/violet/indigo theme */}
          <linearGradient id="darkGrad1" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop
              offset="0%"
              style={{ stopColor: "rgb(130, 90, 200)", stopOpacity: 0.8 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: "rgb(100, 70, 170)", stopOpacity: 0.4 }}
            />
          </linearGradient>
          <linearGradient id="darkGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop
              offset="0%"
              style={{ stopColor: "rgb(120, 100, 190)", stopOpacity: 0.7 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: "rgb(90, 70, 160)", stopOpacity: 0.4 }}
            />
          </linearGradient>
        </defs>
        <path
          d="M1200,0 Q900,40 600,20 T0,0"
          stroke="url(#lightGrad1)"
          className="light-mode-stroke"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
        >
          <animate
            attributeName="d"
            dur="20s"
            repeatCount="indefinite"
            values="M1200,0 Q900,40 600,20 T0,0;M1200,0 Q900,60 600,30 T0,0;M1200,0 Q900,40 600,20 T0,0"
          />
        </path>
        <path
          d="M1200,0 Q800,80 400,60 T0,0"
          stroke="url(#lightGrad2)"
          className="light-mode-stroke"
          strokeWidth="1.2"
          fill="none"
          strokeLinecap="round"
        >
          <animate
            attributeName="d"
            dur="25s"
            repeatCount="indefinite"
            values="M1200,0 Q800,80 400,60 T0,0;M1200,0 Q800,100 400,70 T0,0;M1200,0 Q800,80 400,60 T0,0"
          />
        </path>
        <path
          d="M1200,150 Q900,80 600,140 T0,150"
          stroke="url(#lightGrad1)"
          className="light-mode-stroke"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          opacity="0.6"
        >
          <animate
            attributeName="d"
            dur="18s"
            repeatCount="indefinite"
            values="M1200,150 Q900,80 600,140 T0,150;M1200,140 Q900,100 600,150 T0,140;M1200,150 Q900,80 600,140 T0,150"
          />
        </path>
        <line x1="1200" y1="0" x2="850" y2="220" stroke="#0066cc" strokeWidth="1" opacity="0.4">
          <animate attributeName="x2" dur="15s" repeatCount="indefinite" values="850;800;850" />
          <animate attributeName="y2" dur="15s" repeatCount="indefinite" values="220;190;220" />
        </line>
        <line x1="450" y1="0" x2="0" y2="240" stroke="#0099ff" strokeWidth="1" opacity="0.4">
          <animate attributeName="x1" dur="18s" repeatCount="indefinite" values="450;500;450" />
        </line>
        <line x1="1050" y1="240" x2="650" y2="0" stroke="#5555ff" strokeWidth="1" opacity="0.35">
          <animate attributeName="y1" dur="22s" repeatCount="indefinite" values="240;260;240" />
        </line>
        <line x1="750" y1="0" x2="750" y2="120" stroke="#0088cc" strokeWidth="1" opacity="0.3">
          <animate attributeName="y2" dur="16s" repeatCount="indefinite" values="120;140;120" />
        </line>
        <circle cx="950" cy="100" r="4" fill="#0066cc" opacity="0.5">
          <animate attributeName="opacity" dur="3s" repeatCount="indefinite" values="0.5;0.9;0.5" />
        </circle>
        <circle cx="550" cy="210" r="4" fill="#0099ff" opacity="0.5">
          <animate attributeName="opacity" dur="4s" repeatCount="indefinite" values="0.5;0.9;0.5" />
        </circle>
        <circle cx="750" cy="150" r="4" fill="#5555ff" opacity="0.5">
          <animate attributeName="opacity" dur="3.5s" repeatCount="indefinite" values="0.5;0.9;0.5" />
        </circle>
        <circle cx="350" cy="120" r="4" fill="#0088cc" opacity="0.5">
          <animate attributeName="opacity" dur="3.2s" repeatCount="indefinite" values="0.5;0.9;0.5" />
        </circle>
      </svg>

    </>
  );
};

export default GradientEffects;
